import type {
  HousingFilters,
  Listing,
  SearchIntent,
} from '../types/housing'

export const DEFAULT_FILTERS: HousingFilters = {
  maxMinutes: 45,
  facilities: [],
  occupants: null,
  verifiedOnly: false,
  sort: 'recommended',
}

function recommendationScore(listing: Listing, intent: SearchIntent): number {
  const affordability = Math.max(0, 1 - listing.rentPerYear / intent.budgetMax)
  const proximity = Math.max(0, 1 - listing.minutesToCampus / 60)
  const trust = listing.verification === 'verified' ? 0.25 : 0
  const quality = (listing.rating / 5) * 0.2
  return affordability * 0.35 + proximity * 0.3 + trust + quality
}

export function applyHousingSearch(
  listings: Listing[],
  intent: SearchIntent,
  filters: HousingFilters,
): Listing[] {
  const matched = listings.filter((listing) => {
    if (listing.campusId !== intent.campusId) return false
    if (listing.rentPerYear > intent.budgetMax) return false
    if (intent.kinds.length > 0 && !intent.kinds.includes(listing.kind)) return false
    if (listing.minutesToCampus > filters.maxMinutes) return false
    if (filters.verifiedOnly && listing.verification !== 'verified') return false
    if (filters.occupants !== null && listing.occupants !== filters.occupants) return false
    if (
      filters.facilities.length > 0 &&
      !filters.facilities.every((facility) => listing.facilities.includes(facility))
    ) {
      return false
    }
    return true
  })

  const sorted = [...matched]
  if (filters.sort === 'price_asc') {
    sorted.sort((a, b) => a.rentPerYear - b.rentPerYear)
  } else if (filters.sort === 'distance') {
    sorted.sort((a, b) => a.minutesToCampus - b.minutesToCampus)
  } else {
    sorted.sort(
      (a, b) => recommendationScore(b, intent) - recommendationScore(a, intent),
    )
  }
  return sorted
}

export function verificationCopy(listing: Listing): {
  label: string
  note: string
} {
  if (listing.verification === 'verified') {
    return {
      label: 'Verified listing',
      note: `Campuzon checked ${listing.verifiedChecks.join(', ').toLowerCase()}.`,
    }
  }
  if (listing.verification === 'pending') {
    return {
      label: 'Verification in progress',
      note: 'Documents submitted, review not yet complete.',
    }
  }
  return {
    label: 'Unverified listing',
    note: 'Not inspected by Campuzon. Visit in person before paying anything.',
  }
}
